/**
 * @file useAuditLog.ts
 * @description Real-time Firestore listener for the reconciliation_audit_log collection.
 */

import { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { AuditLogEntry } from "../types";
import { MOCK_AUDIT_LOG } from "../data/mockData";

const MOCK_MODE = import.meta.env.VITE_USE_EMULATORS !== "true" &&
  !import.meta.env.VITE_FIREBASE_PROJECT_ID?.includes("your-project");

interface UseAuditLogResult {
  entries: AuditLogEntry[];
  loading: boolean;
  error: string | null;
}

export function useAuditLog(maxEntries = 100): UseAuditLogResult {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (MOCK_MODE) {
      setEntries(MOCK_AUDIT_LOG);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "reconciliation_audit_log"),
      orderBy("timestamp", "desc"),
      limit(maxEntries)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          ...(doc.data() as AuditLogEntry),
          id: doc.id,
        }));
        setEntries(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("[useAuditLog] Firestore error:", err);
        setError(`Failed to load audit log: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [maxEntries]);

  return { entries, loading, error };
}
